import { Component } from '@theme/component';

/**
 * The "Toate categoriile" panel from "Davitta Navbar v2" state 03 — the whole
 * two-level category tree in one sheet under the header.
 *
 * It is the entry point to the taxonomy wherever the category row cannot be:
 * once the header is sticky (the row collapses) and below 1024px (the row is
 * hidden). The panel itself is plain markup rendered from the same linklist as
 * the category row, so this component only owns open/close state.
 *
 * Triggers live elsewhere in the header — they reach this element by id via
 * `on:click="#davitta-nav-panel/toggle"` — so they are located here by their
 * `aria-controls`, and their `aria-expanded` is mirrored from the sheet.
 *
 * Focus is deliberately not trapped: the design keeps the search field, account
 * and cart usable with the panel open, so the panel behaves like a header
 * disclosure and closes when focus leaves it (per "closes on Esc, outside
 * click, or blur") rather than holding the keyboard hostage.
 *
 * @typedef {object} NavPanelRefs
 * @property {HTMLElement} sheet - The panel surface, toggled via the `hidden` attribute
 *
 * @extends {Component<NavPanelRefs>}
 */
class DavittaNavPanel extends Component {
  requiredRefs = ['sheet'];

  connectedCallback() {
    super.connectedCallback();

    // All three listeners sit on the document because the panel reacts to
    // activity outside itself, including on the trigger that opened it.
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('pointerdown', this.#onPointerDown, { capture: true });
    document.addEventListener('focusin', this.#onFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('pointerdown', this.#onPointerDown, { capture: true });
    document.removeEventListener('focusin', this.#onFocusIn);
  }

  /** @returns {boolean} Whether the panel is open */
  get isOpen() {
    return !this.refs.sheet.hasAttribute('hidden');
  }

  /** @returns {HTMLElement[]} Every trigger pointing at this panel's sheet */
  get #triggers() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.refs.sheet.id}"]`));
  }

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.show();
    }
  };

  show = () => {
    if (this.isOpen) return;

    this.refs.sheet.removeAttribute('hidden');
    for (const trigger of this.#triggers) trigger.setAttribute('aria-expanded', 'true');
  };

  /**
   * @param {boolean} [restoreFocus] - Move focus back to the visible trigger. Skipped
   *   when the panel closes because focus or a pointer went somewhere else on purpose.
   */
  close = (restoreFocus = false) => {
    if (!this.isOpen) return;

    this.refs.sheet.setAttribute('hidden', '');
    for (const trigger of this.#triggers) trigger.setAttribute('aria-expanded', 'false');

    if (!restoreFocus) return;

    // More than one trigger can exist (sticky row, narrow viewport); focus the
    // one actually on screen so keyboard users do not land nowhere.
    this.#triggers.find((element) => element.offsetParent !== null)?.focus();
  };

  /** @param {Node} node */
  #contains(node) {
    return this.refs.sheet.contains(node) || this.#triggers.some((trigger) => trigger.contains(node));
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    this.close(true);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!this.isOpen || !(event.target instanceof Node)) return;

    // A press on a trigger is the trigger's own business — closing here would
    // race its click handler and make the toggle look dead.
    if (this.#contains(event.target)) return;

    this.close();
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (!this.isOpen || !(event.target instanceof Node)) return;
    if (this.#contains(event.target)) return;

    this.close();
  };
}

if (!customElements.get('davitta-nav-panel-component')) {
  customElements.define('davitta-nav-panel-component', DavittaNavPanel);
}
